import {
  CAAS_INDEX_SUFFIX,
  DISABLE_ADS,
  IS_DEV_SITE,
} from "@src/app/constants";
import {
  GA4_REF_CODE_CONTENTBLOCK_B,
  GA4_REF_CODE_CONTENTBLOCK_C,
  GA4_REF_CODE_CONTENTBLOCK_J,
  GA4_REF_CODE_CONTENTBLOCK_L,
} from "@src/app/utils/ga/ref-code";
import {
  BRIGHTCOVE_CONFIG,
  DEFAULT_CONFIG_GAM,
  LOTAME_CONFIG,
  VWO_CONFIG,
} from "@src/pubs/common/config";

import {
  ConfigAboutUs,
  ConfigAffiliateLibrary,
  ConfigAffiliatesDeclaration,
  ConfigAffiliateShopping,
  ConfigAffiliatesProduct,
  ConfigAmazonAffiliates,
  ConfigAuthorProfile,
  ConfigBrightcove,
  ConfigCaas,
  ConfigCategory,
  ConfigContentHub,
  ConfigError,
  ConfigExploreMore,
  ConfigGAM,
  ConfigGTM,
  ConfigHome,
  ConfigLatestStory,
  ConfigLotame,
  ConfigNewsletter,
  ConfigRss,
  ConfigSearch,
  ConfigSingleStory,
  ConfigSite,
  ConfigSkimlinks,
  ConfigTaboola,
  ConfigTag,
  ConfigTeaser,
  ConfigTechshow,
  ConfigTrackingCode,
  ConfigVideoLanding,
  ConfigVideoOverlayPortrait,
  ConfigVWO,
} from "../types/config";
import image404 from "./assets/404.webp";
import { configProductStoryRatings } from "./config/configProductStoryRatings";
import { configProductStorySpecifications } from "./config/configProductStorySpecifications";
import Banner from "./Home/Banner";
import HwzForumChatter from "./Home/HwzForumChatter";
import { aiSummaryIcon } from "./icons/AiSummaryIcon";
import callOnQuoteSvg from "./icons/callOnQuote.svg?url";
import callOnQuoteSvgBtm from "./icons/callOnQuoteBtm.svg?url";
import { cartIcon } from "./icons/cart";
import { moreIcon } from "./icons/moreIcon";
import logoPath from "./logo.svg?url";
import TechshowPortalBlock from "./routes/TechshowPortal/TechshowPortalBlock";
import TechAwardsHighlights from "./TechAwards/TechAwardsHighlights";

export const configSite: ConfigSite = {
  name: "HardwareZone Singapore",
  address:
    import.meta.env.VITE_PUBLIC_BASE_URL || "https://www.hardwarezone.com.sg", // without trailing slash
  tagline: "Technology News, Products & Gadgets Reviews, Latest Tech Updates",
  description:
    "HardwareZone is the leading online technology portal in Asia Pacific gives you latest tech Updates, technology news, products & gadgets reviews and more.",
  titleSeparator: "|",
  logoPath,
  sectionLevel3: true,
  twitterSite: "@hardwarezone",
  pageShare: {
    size: 35,
    media: [
      { name: "Facebook" },
      { name: "Whatsapp" },
      { name: "Email" },
      { name: "LinkedIn" },
      { name: "X" },
    ],
  },
  meta: [
    { name: "msapplication-TileColor", content: "#000000" },
    { name: "theme-color", content: "#ffffff" },
  ],
  links: [
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/favicon-32x32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/favicon-16x16.png",
    },
  ],
};

export { configHeader } from "./config/configHeader";

export const configCaas: ConfigCaas = {
  index: `hardwarezone${CAAS_INDEX_SUFFIX}`,
  tagIdPrefix: "tag:hardwarezone-keywords@sph.com.sg,2023:",
  cuePublicationName: "hardwarezone",
};

export const configGTM: ConfigGTM = {
  container: "GTM-N58WWMC",
  siteVerification: "_cGEdj2ZMeQhb_OTIAKvlCGbSxaBwct6M6XrDxywcXM",
};

export const configGAM: ConfigGAM = {
  ...DEFAULT_CONFIG_GAM,
  account: "/5908/hwz-sg",
  debounce: true,
  categoryPrefix: "ros",
  outbrainIds: ["AR_2", "AR_3"],
  outbrainIdsAlt: ["AR_2", "AR_3"],
  enableAbm: false,
};

export const configLotame: ConfigLotame = {
  ...LOTAME_CONFIG,
  active: !DISABLE_ADS,
};

export const configSkimlinks: ConfigSkimlinks = {
  active: !DISABLE_ADS,
  skimlinkId: "157725X1624231",
  geniuslinkId: "319902",
};

export const configVWO: ConfigVWO = {
  ...VWO_CONFIG,
  active: true,
};

export const configBrightcove: ConfigBrightcove = { ...BRIGHTCOVE_CONFIG };

export const configSingleStory: ConfigSingleStory = {
  authorProfile: true,
  caption: { prefix: "Credit: " },
  byline: {
    defaultCredit: "HardwareZone Team",
    moveShareAndBylineToDescription: true,
  },
  configShareArticle: { label: "Share this article", divider: { top: false } },
  featureStory: { skipCoverCaption: true, bodyMaxWidth: 1000 },
  gallery: {
    paginationSeparator: "/",
    galleryItemShareMedia: ["Facebook", "X", "Whatsapp", "Pinterest"],
  },
  quote: { svg: callOnQuoteSvg, svgBtm: callOnQuoteSvgBtm },
  displayTags: { displayBottomTags: true, displayTagHeading: true },
  productStoryRatings: configProductStoryRatings,
  productStorySpecifications: configProductStorySpecifications,
  teaserWithProductSpec: true,
  htmlInTableBody: true,
  productContent: { hideProsCons: true },
  aiSummary: {
    enabled: true,
    titleAtHeader: true,
    aiIcon: aiSummaryIcon,
  },
};

export const configContentHub: ConfigContentHub = {
  configAds: {
    leaderboardAbove: true,
    showInlineAds: true,
  },
  configMasthead: {
    heading: { display: false },
    featuredImageAlwaysOriginal: true,
  },
  configBodyContent: { withHeading: true, display: true, hasAd: true },
  configShareButton: { display: true, label: "Share this" },
  configEditorsNote: { display: false },
  configCallQoute: { display: true },
  configFeaturedSection: { display: true },
  configDeals: {
    display: false,
    props: { heading: "editor's picks", noOfItemsInFrame: 4 },
  },
  storiesPerPage: {
    topStories: 5,
    totalStories: 11,
  },
  configTopStories: {
    display: true,
    firstItemAnchorBlock: true,
    anchorBlockDisplayType: "row",
    anchorBlockAspectRatio: 3 / 2,
  },
  configListingStories: { mode: "landscape", dateAfter: true },
  layout: {
    stories: {
      elements: [
        {
          Component: TechAwardsHighlights,
          name: "contentListBlock",
        },
        {
          Component: TechshowPortalBlock,
          name: "contentListBlock",
          limit: 5,
        },
        { name: "adsMidContent" },
      ],
    },
    columnImages: { imageCrop: "original" },
  },
};

export const configLatestStory: ConfigLatestStory = {
  latestStoryLimit: 13,
  subAnchorLimit: 5,
  landscapeIndexes: [0, 1, 2, 3],
  mainContentBlockDisplay: "grid",
  mainContentBlockMode: "landscape",
  errorLayoutMode: "landscape",
  portraitImageWidth: 285,
  landscapeImageWidth: 345,
  hasAnchorBlock: true,
  customTitle: "Latest",
};

export const configCategory: ConfigCategory = {
  pinnedTeasersLimit: 4,
  latestStoriesLimit: 8,
  adPositioning: 3,
  filterBy: {
    label: "Filter by:",
    options: [
      // ToDo: correct HWZ story types
      { pathSuffix: "news", text: "NEWS", storyType: "techNews" },
      { pathSuffix: "reviews", text: "REVIEWS", storyType: "reviewStory" },
      { pathSuffix: "features", text: "FEATURES", storyType: "featureStory" },
    ],
  },
  card: {
    anchorblock: {
      defaultVariant: "anchorblock-stacked",
      mobileUp: {
        defaultVariant: "anchorblock-list-mobile-up",
      },
    },
    subAnchor: {
      defaultVariant: "card-list-original-border",
      mobileUp: {
        defaultVariant: "card-stacked-landscape-mobile-up",
      },
    },
    latestArticle: {
      defaultVariant: "card-list-original-border",
      mobileUp: {
        defaultVariant: "card-stacked-landscape-mobile-up",
      },
    },
  },
};

export const configTag: ConfigTag = {
  topStoriesLimit: 5,
  listedStoriesLimit: 13,
};

export const configAmazonAffiliates: ConfigAmazonAffiliates = {
  partnerTag: IS_DEV_SITE ? "sphmedia-dev-22" : "sphmedia-hwz-22",
};

export const configTaboola: ConfigTaboola = {
  enabled: true,
  publisherId: "sphmedia-hardwarezonesg",
};

export const configSearch: ConfigSearch = {};

export const configExploreMore: ConfigExploreMore = {
  heading: "EXPLORE OTHER TOPICS",
};

export const configVideoLanding: ConfigVideoLanding = {
  videoPlaylistFillUpSize: 9,
  videoPath: "/video",
};

export const configVideoOverlayPortrait: ConfigVideoOverlayPortrait = {};

export const configHome: ConfigHome = {
  teaser: { displayPublishDate: true },
  headingDivider: true,
  isHeadingClickable: true,
  viewMore: { icon: moreIcon },
  latestArticle: {
    mobileMode: "landscape",
    landscapeIndexes: [0, 1, 2, 3],
    viewMore: { text: "Read More" },
  },
  contentBlockB: {
    cropsByIndex: [
      [0, "landscape"],
      [1, "square"],
      [2, "landscape"],
    ],
    itemCenteredIndex: 1,
  },
  contentBlockF: {
    skipSectionContext: true,
    landscapeIndexes: [0, 1, 2, 3],
    layoutTwoColumn: [0, 1, 2, 3],
    landscapeImageWidth: 630,
    twoColumnsLandscapeImageWidth: 300,
    viewMore: { text: "View more" },
  },
  contentBlockD: { descriptionInTitle: false, crop: "landscape" },
  videoGalleryLandscape: {
    hasPagination: true,
    spotLight: { mobileLoopingArrowsBelow: true },
  },
  contentColumnSideScroll: { section: "shopping" },
  affiliateProduct: { description: "", carouselWidthLeeWay: 11 },
  magazineListing: { midDivider: false },
  newsletter: {
    newsletterTitle: `Connect with us and stay updated with the latest news and promotions from us!`,
    newsletterCtaText: "Sign Up",
  },
  trending: { limit: 4 },
  layoutGrid: {
    elements: [
      {
        name: "anchorBlock",
        dataSource: {
          type: "list",
          key: "anchorblock",
          fallback: "latest",
          limit: 1,
        },
      },
      {
        name: "latestArticle",
        dataSource: {
          type: "list",
          key: "pinned",
          fallback: "latest",
          limit: 4,
        },
      },
      {
        name: "contentBlockD",
        dataSource: {
          type: "list",
          key: "trending",
          fallback: "trending",
          title: "Trending",
          refCode: "trending",
          limit: 3,
        },
        alternateBg: true,
        trendingBlock: true,
      },
      { name: "adUnit" },
      {
        name: "contentBlockGridAreas",
        dataSource: {
          type: "section",
          key: "samsung",
          refCode: "block-d",
          limit: 5,
          fallback: "tag",
        },
      },
      {
        Component: Banner,
        dataSource: { type: "list", key: "spotlight_story", limit: 1 },
      },
      {
        name: "contentBlockD",
        dataSource: {
          type: "section",
          key: "editors-picks", // Temporary for now and replace with "Top Tech Reviews" when ready
          refCode: "block-b",
          limit: 3,
          fallback: "tag",
        },
      },
      {
        name: "affiliateProduct",
        dataSource: { type: "list", key: "deals" },
        alternateBg: true,
      },
      {
        name: "contentBlockGridAreas",
        dataSource: {
          type: "section",
          key: "apple",
          refCode: "block-c",
          limit: 5,
          fallback: "tag",
        },
      },
      {
        name: "contentBlockGridAreas",
        dataSource: {
          type: "section",
          key: "lifestyle",
          refCode: "block-d",
          limit: 5,
        },
      },
      {
        name: "contentBlockF",
        dataSource: {
          type: "section",
          key: "deals",
          refCode: "block-e",
          limit: 4,
        },
        alternateBg: true,
      },
      {
        name: "contentBlockJ",
        dataSource: {
          type: "section",
          key: "entertainment_gaming",
          refCode: "block-f",
          limit: 4,
        },
        alternateBg: true,
        trendingBlock: false,
      },
      {
        name: "videoGalleryLandscape",
        dataSource: {
          type: "list",
          key: "videoplaylist",
          refCode: "videoplaylist",
          fallback: "videoplaylist",
          limit: 8,
        },
      },
      {
        name: "socialVideoPortrait",
        dataSource: { type: "list", key: "socialvideos" },
      },
      {
        Component: HwzForumChatter,
        dataSource: {
          type: "list",
          key: "hwz_forum_chatter",
          limit: 3,
          refCode: "latestforumchatter",
        },
      },
      { name: "magazineListing" },
      { name: "newsletter" },
    ],
  },
  trendingStories: { publisher: "hwz" },
  anchorBlock: { displayType: "row" },
  contentBlockGridAreas: {
    mode: "landscape",
    displayType: "row",
    viewMore: { text: "View more" },
  },
  contentBlockJ: {
    anchorBlock: { displayType: "row" },
    viewMore: { text: "View more" },
  },
};

export const configNewsletter: ConfigNewsletter = {
  title: `Connect with us and stay updated with the latest news and promotions from us!`,
  subscriptionMessage: {
    title: "Thank you for subscribing!",
    body: `Welcome to our community! You're all set to receive the latest updates, exclusive content, and insights delivered straight to your inbox.

Keep an eye out for your first newsletter hitting your inbox soon!`,
  },
};

export const configTeaser: ConfigTeaser = {
  publishDate: true,
  dateAfter: true,
  anchorBlock: { publishDate: false, mobileCrop: "landscape" },
};

export const configTrackingCode: ConfigTrackingCode = {
  category: { mainContentBlock: GA4_REF_CODE_CONTENTBLOCK_L },
  exploremore: "exploremore",
  contenthub: {
    topstories: {
      contentBlockB: GA4_REF_CODE_CONTENTBLOCK_B,
      contentBlockF: GA4_REF_CODE_CONTENTBLOCK_J,
    },
    featured: GA4_REF_CODE_CONTENTBLOCK_C,
  },
};

export const configAffiliatesDeclaration: ConfigAffiliatesDeclaration = {
  text: "Our articles may contain affiliate links. If you buy through these links, we may earn a small commission.",
  disclaimer:
    "All products have been vetted by the SPH Media shopping team. We may earn an affiliate commission if you buy through our links.",
};

export const configError: ConfigError = {
  relatedTitle: "Perhaps what you are looking for is here.",
  messages: {
    status404: {
      title:
        "Uh-oh, the page that you’re looking for could not be found. Please re-check the URL address.",
      image: { src: image404, srcWidth: 225, srcHeight: 225 },
    },
  },
};

export const configAuthorProfile: ConfigAuthorProfile = {
  showReadMore: true,
  hideDivider: true,
  listingLayout: {
    mainContentBlockDisplay: "grid",
    mainContentBlockMode: "landscape",
    imageSize: "big",
  },
  hideAds: true,
  hideSidebar: true,
};

export const configAboutUs: ConfigAboutUs = {
  profileBlock: { heading: "Meet The Team" },
};

export const configAffiliatesProduct: ConfigAffiliatesProduct = {
  imageCrop: "landscape",
  cta: { label: "Shop Now", buttonIcon: cartIcon },
  itemWidth: 208,
};

export const configAffiliateShopping: ConfigAffiliateShopping = {
  withDivider: true,
  layoutGrid: {
    elements: [
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["list"],
          key: "products-7-grid",
          limit: 7,
          config: {
            cta: { name: "Shop more in story" },
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-stacked",
                mobileUp: { defaultVariant: "anchorblock-list-mobile-up" },
              },
            },
            moreStories: {
              ads: { sidebar: true },
              cardLayout: {
                defaultVariant: "card-stacked-original",
                mobileUp: { defaultVariant: "card-list-landscape-mobile-up" },
              },
            },
            productMobileLayout: "list",
          },
        },
      },
      {
        name: "products",
        dataSource: { types: ["list"], key: "products-6-grid", limit: 6 },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section"],
          key: "stories-4",
          limit: 4,
          config: {
            backgroundColor: "#000000",
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-stacked",
                mobileUp: { defaultVariant: "anchorblock-list-mobile-up" },
              },
            },
            moreStories: { ads: { sidebar: true } },
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["list"],
          key: "products-4-grid",
          limit: 4,
          config: {
            cta: { name: "Shop more in story" },
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-stacked",
                mobileUp: { defaultVariant: "anchorblock-list-mobile-up" },
              },
            },
            productMobileLayout: "grid",
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section", "tag"],
          key: "stories-3",
          limit: 3,
          config: {
            backgroundColor: "#000000",
            moreStories: {
              isLandscape: true,
              cardLayout: {
                defaultVariant: "card-stacked-original",
                mobileUp: {
                  defaultVariant: "card-stacked-landscape-mobile-up",
                },
              },
              layout: { mobileUp: "stacked" },
            },
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section", "tag"],
          key: "stories-12",
          limit: 12,
          config: {
            cta: { name: "View all deals" },
            moreStories: {
              isLandscape: true,
              hasCta: true,
              ads: { sidebar: false, leaderboard: true },
              cardLayout: {
                defaultVariant: "card-stacked-landscape",
                mobileUp: {
                  defaultVariant: "card-stacked-landscape-mobile-up",
                },
              },
              layout: { mobileUp: "stacked" },
            },
          },
        },
      },
    ],
  },
  viewMore: { icon: moreIcon },
};

export const configRss: ConfigRss = [
  {
    category: "all",
    size: 20,
    storyTypes: [],
    sections: [],
    encloseImageCrop: "landscape",
  },
  {
    category: "news",
    size: 20,
    storyTypes: ["techNews"],
    sections: [],
    encloseImageCrop: "landscape",
  },
  {
    category: "features",
    size: 20,
    storyTypes: ["reviewStory", "featureStory"],
    sections: [],
    encloseImageCrop: "landscape",
  },
  {
    category: "tv",
    size: 20,
    storyTypes: ["videoStory"],
    sections: [],
    encloseImageCrop: "landscape",
  },
  {
    category: "blogs",
    size: 20,
    storyTypes: ["blogStory"],
    sections: [],
    encloseImageCrop: "landscape",
  },
  {
    category: "gaming",
    size: 20,
    storyTypes: [],
    sections: [
      "entertainment",
      "entertainment_gaming",
      "entertainment_tv-movies",
    ],
    encloseImageCrop: "landscape",
  },
  //for forum sections
  {
    category: "digital-cameras-photography",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["lifestyle_photography"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "cars-cars",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["lifestyle_cars"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "eat-drink-man-woman",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["guides", "guides_how-to", "deals"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "gaming-arena",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["entertainment_gaming"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "hardware-clinic",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["pc", "pc_components", "pc_accessories", "pc_storage"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "headphones-earphones-and-portable-media-devices",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["lifestyle_audio"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "home-decor-furnishings",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["lifestyle_home"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "internet-bandwidth-networking-clinic",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["pc_networking"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "mobile-communication-technology",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["mobile", "mobile_smartphones", "mobile_telco"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "moviemania",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["entertainment_tv-movies"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "notebook-clinic",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["pc_laptops"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "software-clinic",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["lifestyle_apps"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "the-house-of-displays",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["lifestyle_video"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "the-tablet-den",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["mobile_tablets"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
  {
    category: "wearable-gadgets-and-iot",
    size: 3,
    storyTypes: ["techNews", "reviewStory", "featureStory", "videoStory"],
    sections: ["mobile_wearables"],
    encloseImageCrop: "landscape",
    postToForum: true,
  },
];

export const configTechshow: ConfigTechshow = {
  brochureListing: {
    title: "Brochures",
    description:
      "View all the brochures and offers of the respective brands and participants of the ongoing Tech Show right here. Filter the content to show either by brands, by categories or a combination of both.",
  },
  brochureView: {
    title: "Brochures",
    description:
      "View all the brochures and offers of the respective brands and participants of the ongoing Tech Show right here.",
  },
};

export const configAffiliateLibrary: ConfigAffiliateLibrary = {
  enable: true,
  disclaimerLabel:
    "We may earn an affiliate commission if you buy through our links.",
};
